import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Divider,
  Badge,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  LinearProgress,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText as MuiListItemText,
  ListItemSecondaryAction,
  Tabs,
  Tab,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Campaign as CampaignIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Error as ErrorIcon,
  Public as PublicIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Analytics as AnalyticsIcon,
  Gavel as TargetIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon,
  CloudUpload as CloudUploadIcon,
  Article as ArticleIcon,
  ContentPaste as ContentPasteIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Delete as DeleteMultimediaIcon
} from '@mui/icons-material';
import Logo from '../common/Logo';
import * as campaignApi from '../../services/campaignApi';
import * as prApi from '../../services/prApi';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiConfig';

interface Campaign {
  id: string;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'cancelled';
  startDate: string;
  endDate: string;
  budget: number;
  spent: number;
  targetAudience: string;
  channels: number;
  prs: number;
  impressions: number;
  clicks: number;
  conversions: number;
  ctr: number;
  cpc: number;
  roi: number;
  createdBy: string;
  tags: string[];
  pressReleases?: any[];
}

const Campaigns: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Form state for creating campaigns
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    budget: '',
    targetAudience: '',
    tags: [] as string[]
  });

  // Form state for editing campaigns
  const [editCampaign, setEditCampaign] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    budget: '',
    targetAudience: '',
    tags: [] as string[]
  });

  // PR selection state for edit dialog
  const [editAvailablePRs, setEditAvailablePRs] = useState<any[]>([]);
  const [editSelectedPRs, setEditSelectedPRs] = useState<string[]>([]);

  // PR Upload workflow state
  const [prUploadDialogOpen, setPrUploadDialogOpen] = useState(false);
  const [createdCampaignId, setCreatedCampaignId] = useState<string | null>(null);
  const [createdCampaignDetails, setCreatedCampaignDetails] = useState<any>(null); // Store campaign details
  const [uploadStep, setUploadStep] = useState(0); // 0: upload, 1: select
  const [uploadedPRs, setUploadedPRs] = useState<any[]>([]);
  const [availablePRs, setAvailablePRs] = useState<any[]>([]);
  const [selectedPRs, setSelectedPRs] = useState<string[]>([]);
  const [uploadMethod, setUploadMethod] = useState<'upload' | 'paste'>('upload');
  const [pasteContent, setPasteContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [multimediaFiles, setMultimediaFiles] = useState<{ [prId: string]: string[] }>({}); // Store image URLs per PR
  const [uploadingMultimedia, setUploadingMultimedia] = useState<{ [prId: string]: boolean }>({});

  // Fetch campaigns from API
  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Auto-refresh campaigns when page becomes visible (user returns from other pages)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchCampaigns();
      }
    };

    const handleFocus = () => {
      fetchCampaigns();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await campaignApi.getCampaigns();
      setCampaigns(data || []);
      setFilteredCampaigns(data || []);
    } catch (err: any) {
      console.error('Error fetching campaigns:', err);
      setError(err.message || 'Failed to load campaigns');
      // Set empty arrays to show UI with 0 values instead of blank
      setCampaigns([]);
      setFilteredCampaigns([]);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to load campaigns',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter campaigns
  useEffect(() => {
    let filtered = campaigns;

    if (searchTerm) {
      filtered = filtered.filter(campaign => 
        campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        campaign.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(campaign => campaign.status === statusFilter);
    }

    setFilteredCampaigns(filtered);
  }, [searchTerm, statusFilter, campaigns]);

  const handleCreateCampaign = async () => {
    try {
      if (!newCampaign.name || !newCampaign.startDate || !newCampaign.endDate || !newCampaign.budget || !newCampaign.targetAudience) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required fields',
          severity: 'error'
        });
        return;
      }

      const response = await campaignApi.createCampaign({
        name: newCampaign.name,
        description: newCampaign.description,
        startDate: newCampaign.startDate,
        endDate: newCampaign.endDate,
        budget: parseFloat(newCampaign.budget),
        targetAudience: newCampaign.targetAudience,
        tags: newCampaign.tags
      });

      setSnackbar({
        open: true,
        message: 'Campaign created successfully',
        severity: 'success'
      });

      setCreateDialogOpen(false);
      // Extract campaign ID from response
      // The API returns a Campaign object with 'id' property
      const campaignId = response.id || (response as any)._id;
      if (!campaignId) {
        console.error('Campaign ID not found in response:', response);
        setSnackbar({
          open: true,
          message: 'Campaign created but ID not found. Please refresh the page.',
          severity: 'error'
        });
        return;
      }
      setCreatedCampaignId(campaignId);
      
      // Store campaign details for distribution page
      setCreatedCampaignDetails({
        id: campaignId,
        name: newCampaign.name,
        description: newCampaign.description,
        startDate: newCampaign.startDate,
        endDate: newCampaign.endDate,
        budget: parseFloat(newCampaign.budget),
        targetAudience: newCampaign.targetAudience
      });
      
      // Open PR upload dialog
      setPrUploadDialogOpen(true);
      setUploadStep(0);
      setSelectedPRs([]);
      setUploadedPRs([]);
      setPasteContent('');
      
      // Fetch available PRs
      await fetchAvailablePRs();
      
      setNewCampaign({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        budget: '',
        targetAudience: '',
        tags: []
      });
      
      fetchCampaigns();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to create campaign',
        severity: 'error'
      });
    }
  };

  const fetchAvailablePRs = async () => {
    try {
      const prs = await prApi.getPressReleases();
      // Filter out PRs that are already in uploadedPRs to avoid duplicates
      const uploadedPrIds = new Set(uploadedPRs.map(pr => String(pr._id).trim()));
      const uniquePRs = prs.filter(pr => {
        const prId = String(pr._id).trim();
        return !uploadedPrIds.has(prId);
      });
      setAvailablePRs(uniquePRs);
    } catch (err: any) {
      console.error('Error fetching PRs:', err);
    }
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append('files', file);
      });

      const token = localStorage.getItem('token');
      const API_BASE_URL = getApiBaseUrl();
      
      const response = await axios.post(`${API_BASE_URL}/pr/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.prId) {
        const prId = String(response.data.prId).trim();
        // Check if PR already exists to avoid duplicates
        const existingPrIds = new Set(uploadedPRs.map(pr => String(pr._id || pr.id).trim()));
        if (!existingPrIds.has(prId)) {
          const newPR = {
            _id: prId,
            id: prId,
            title: response.data.title || 'New Press Release',
            status: 'draft'
          };
          setUploadedPRs(prev => {
            // Ensure no duplicates
            const existingIds = new Set(prev.map(p => String(p._id || p.id).trim()));
            if (existingIds.has(prId)) return prev;
            return [...prev, newPR];
          });
          setSelectedPRs(prev => {
            const normalizedPrev = prev.map(id => String(id).trim());
            if (normalizedPrev.includes(prId)) return prev;
            return [...prev, prId];
          });
          setSnackbar({
            open: true,
            message: 'PR uploaded successfully',
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'PR already uploaded',
            severity: 'success'
          });
        }
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to upload PR',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handlePastePR = async () => {
    if (!pasteContent.trim()) {
      setSnackbar({
        open: true,
        message: 'Please paste PR content',
        severity: 'error'
      });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('content', pasteContent);

      const token = localStorage.getItem('token');
      const API_BASE_URL = getApiBaseUrl();
      
      const response = await axios.post(`${API_BASE_URL}/pr/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.prId) {
        const prId = String(response.data.prId).trim();
        // Check if PR already exists to avoid duplicates
        const existingPrIds = new Set(uploadedPRs.map(pr => String(pr._id || pr.id).trim()));
        if (!existingPrIds.has(prId)) {
          const newPR = {
            _id: prId,
            id: prId,
            title: response.data.title || 'New Press Release',
            status: 'draft'
          };
          setUploadedPRs(prev => {
            const existingIds = new Set(prev.map(p => String(p._id || p.id).trim()));
            if (existingIds.has(prId)) return prev;
            return [...prev, newPR];
          });
          setSelectedPRs(prev => {
            const normalizedPrev = prev.map(id => String(id).trim());
            if (normalizedPrev.includes(prId)) return prev;
            return [...prev, prId];
          });
          setPasteContent('');
          setSnackbar({
            open: true,
            message: 'PR created successfully',
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'PR already exists',
            severity: 'success'
          });
        }
      }
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to create PR',
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleTogglePR = (prId: string) => {
    const normalizedPrId = String(prId).trim();
    setSelectedPRs(prev => {
      const normalizedPrev = prev.map(id => String(id).trim());
      return normalizedPrev.includes(normalizedPrId)
        ? prev.filter(id => String(id).trim() !== normalizedPrId)
        : [...prev, normalizedPrId];
    });
  };

  const handleSelectAllPRs = () => {
    // Get all unique PR IDs, normalized
    const uploadedIds = uploadedPRs.map(pr => String(pr._id || pr.id).trim());
    const availableIds = availablePRs.map(pr => String(pr._id || pr.id).trim());
    const allPRIds = Array.from(new Set([...uploadedIds, ...availableIds]));
    const normalizedSelected = selectedPRs.map(id => String(id).trim());
    const allSelected = allPRIds.every(id => normalizedSelected.includes(id));
    
    if (allSelected) {
      setSelectedPRs([]);
    } else {
      setSelectedPRs(allPRIds);
    }
  };

  const handleMultimediaUpload = async (files: FileList | null, prId: string) => {
    if (!files || files.length === 0) return;

    const normalizedPrId = String(prId).trim();
    setUploadingMultimedia(prev => ({ ...prev, [normalizedPrId]: true }));

    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = getApiBaseUrl();
      const uploadedUrls: string[] = [];

      // Upload each image file to Cloudinary via media API
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) {
          setSnackbar({
            open: true,
            message: 'Only image files are allowed',
            severity: 'error'
          });
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'image');
        formData.append('name', file.name);
        formData.append('usage', 'Press release multimedia');

        const response = await axios.post(`${API_BASE_URL}/api/media/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.asset && response.data.asset.url) {
          uploadedUrls.push(response.data.asset.url);
        }
      }

      if (uploadedUrls.length > 0) {
        setMultimediaFiles(prev => ({
          ...prev,
          [normalizedPrId]: [...(prev[normalizedPrId] || []), ...uploadedUrls]
        }));
        setSnackbar({
          open: true,
          message: `${uploadedUrls.length} image(s) uploaded successfully`,
          severity: 'success'
        });
      }
    } catch (err: any) {
      console.error('Error uploading multimedia:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to upload images',
        severity: 'error'
      });
    } finally {
      setUploadingMultimedia(prev => ({ ...prev, [normalizedPrId]: false }));
    }
  };

  const handleRemoveMultimedia = (prId: string, imageIndex: number) => {
    const normalizedPrId = String(prId).trim();
    setMultimediaFiles(prev => {
      const prImages = prev[normalizedPrId] || [];
      return {
        ...prev,
        [normalizedPrId]: prImages.filter((_, idx) => idx !== imageIndex)
      };
    });
  };

  const handleProceedToDistribution = async () => {
    if (selectedPRs.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one PR to distribute',
        severity: 'error'
      });
      return;
    }

    // Add PRs to campaign
    if (createdCampaignId) {
      try {
        for (const prId of selectedPRs) {
          await campaignApi.addPressReleaseToCampaign(createdCampaignId, prId);
        }
      } catch (err: any) {
        console.error('Error adding PRs to campaign:', err);
      }
    }

    // Store campaign details and PR IDs in localStorage for distribution page
    // Include multimedia URLs for each PR
    const prMultimediaData: { [prId: string]: string[] } = {};
    selectedPRs.forEach(prId => {
      const normalizedPrId = String(prId).trim();
      if (multimediaFiles[normalizedPrId]) {
        prMultimediaData[normalizedPrId] = multimediaFiles[normalizedPrId];
      }
    });

    const campaignData = {
      campaignId: createdCampaignId,
      campaignDetails: createdCampaignDetails,
      prIds: selectedPRs,
      prCount: selectedPRs.length,
      multimedia: prMultimediaData
    };
    
    try {
      localStorage.setItem('campaignDistributionData', JSON.stringify(campaignData));
    } catch (err) {
      console.error('Error storing campaign data:', err);
    }

    // Close dialog
    setPrUploadDialogOpen(false);
    setUploadStep(0);
    const campaignIdToPass = createdCampaignId;
    setCreatedCampaignId(null);
    setCreatedCampaignDetails(null);
    setSelectedPRs([]);
    setUploadedPRs([]);
    setPasteContent('');

    // Navigate to distribution page with selected PRs and campaign info
    const prIdsParam = selectedPRs.join(',');
    const campaignNameParam = encodeURIComponent(createdCampaignDetails?.name || '');
    const startDateParam = encodeURIComponent(createdCampaignDetails?.startDate || '');
    const endDateParam = encodeURIComponent(createdCampaignDetails?.endDate || '');
    navigate(`/distribute?prIds=${prIdsParam}&campaignId=${campaignIdToPass || ''}&campaignName=${campaignNameParam}&startDate=${startDateParam}&endDate=${endDateParam}`);
  };

  const handleDeleteCampaign = async () => {
    if (!selectedCampaign) return;

    try {
      await campaignApi.deleteCampaign(selectedCampaign.id);
      setSnackbar({
        open: true,
        message: 'Campaign deleted successfully',
        severity: 'success'
      });
      handleMenuClose();
      fetchCampaigns();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to delete campaign',
        severity: 'error'
      });
    }
  };

  const handleUpdateStatus = async (status: 'active' | 'paused' | 'cancelled') => {
    if (!selectedCampaign) return;

    const campaignId = selectedCampaign.id;
    handleMenuClose();

    try {
      const updatedCampaign = await campaignApi.updateCampaignStatus(campaignId, status);
      setSnackbar({
        open: true,
        message: `Campaign ${status} successfully`,
        severity: 'success'
      });
      // Refresh campaigns list to show updated status
      await fetchCampaigns();
    } catch (err: any) {
      console.error('Error updating campaign status:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to update campaign status',
        severity: 'error'
      });
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <PlayIcon style={{ color: '#4CAF50' }} />;
      case 'paused': return <PauseIcon style={{ color: '#FF9800' }} />;
      case 'completed': return <CheckCircleIcon style={{ color: '#2196F3' }} />;
      case 'draft': return <EditIcon style={{ color: '#9E9E9E' }} />;
      case 'cancelled': return <StopIcon style={{ color: '#F44336' }} />;
      default: return <PendingIcon style={{ color: '#9E9E9E' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#4CAF50';
      case 'paused': return '#FF9800';
      case 'completed': return '#2196F3';
      case 'draft': return '#9E9E9E';
      case 'cancelled': return '#F44336';
      default: return '#9E9E9E';
    }
  };

  const getBudgetProgress = (spent: number, budget: number) => {
    return Math.min((spent / budget) * 100, 100);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, campaign: Campaign) => {
    setAnchorEl(event.currentTarget);
    setSelectedCampaign(campaign);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCampaign(null);
  };

  const handleViewCampaign = async () => {
    if (!selectedCampaign) return;
    
    const campaignId = selectedCampaign.id;
    handleMenuClose();
    
    try {
      // Fetch full campaign details
      const fullCampaign = await campaignApi.getCampaign(campaignId);
      setSelectedCampaign(fullCampaign);
      setViewDialogOpen(true);
    } catch (err: any) {
      console.error('Error loading campaign details:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to load campaign details',
        severity: 'error'
      });
    }
  };

  const handleEditCampaign = async () => {
    if (!selectedCampaign) return;
    
    const campaignId = selectedCampaign.id;
    handleMenuClose();
    
    try {
      // Fetch full campaign details for editing
      const fullCampaign = await campaignApi.getCampaign(campaignId);
      
      // Fetch available PRs
      const prs = await prApi.getPressReleases();
      setEditAvailablePRs(prs);
      
      // Format dates for input fields (YYYY-MM-DD)
      const formatDate = (dateString: string | Date) => {
        if (!dateString) return '';
        try {
          const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
          if (isNaN(date.getTime())) return '';
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        } catch (err) {
          console.error('Error formatting date:', err);
          return '';
        }
      };
      
      // Get currently selected PR IDs from campaign
      const currentPRIds: string[] = [];
      if ((fullCampaign as any).pressReleases && Array.isArray((fullCampaign as any).pressReleases)) {
        (fullCampaign as any).pressReleases.forEach((pr: any) => {
          const prId = pr._id || pr.id || pr;
          if (prId) {
            currentPRIds.push(typeof prId === 'string' ? prId : String(prId));
          }
        });
      }
      
      setEditCampaign({
        name: fullCampaign.name || '',
        description: fullCampaign.description || '',
        startDate: formatDate(fullCampaign.startDate),
        endDate: formatDate(fullCampaign.endDate),
        budget: fullCampaign.budget?.toString() || '',
        targetAudience: fullCampaign.targetAudience || '',
        tags: fullCampaign.tags || []
      });
      
      setEditSelectedPRs(currentPRIds);
      setSelectedCampaign(fullCampaign);
      setEditDialogOpen(true);
      setViewDialogOpen(false);
    } catch (err: any) {
      console.error('Error loading campaign for editing:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to load campaign for editing',
        severity: 'error'
      });
    }
  };

  const handleUpdateCampaign = async () => {
    if (!selectedCampaign) {
      setSnackbar({
        open: true,
        message: 'No campaign selected',
        severity: 'error'
      });
      return;
    }

    try {
      if (!editCampaign.name || !editCampaign.startDate || !editCampaign.endDate || !editCampaign.budget || !editCampaign.targetAudience) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required fields',
          severity: 'error'
        });
        return;
      }

      const updatedCampaign = await campaignApi.updateCampaign(selectedCampaign.id, {
        name: editCampaign.name,
        description: editCampaign.description,
        startDate: editCampaign.startDate,
        endDate: editCampaign.endDate,
        budget: parseFloat(editCampaign.budget),
        targetAudience: editCampaign.targetAudience,
        tags: editCampaign.tags,
        pressReleases: editSelectedPRs
      });

      setSnackbar({
        open: true,
        message: 'Campaign updated successfully',
        severity: 'success'
      });

      setEditDialogOpen(false);
      setSelectedCampaign(null);
      setEditCampaign({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        budget: '',
        targetAudience: '',
        tags: []
      });
      setEditSelectedPRs([]);
      setEditAvailablePRs([]);
      
      // Refresh campaigns list
      await fetchCampaigns();
    } catch (err: any) {
      console.error('Error updating campaign:', err);
      setSnackbar({
        open: true,
        message: err.message || 'Failed to update campaign',
        severity: 'error'
      });
    }
  };

  const handleToggleEditPR = (prId: string) => {
    setEditSelectedPRs(prev => 
      prev.includes(prId) 
        ? prev.filter(id => id !== prId)
        : [...prev, prId]
    );
  };

  const handleSelectAllEditPRs = () => {
    if (editSelectedPRs.length === editAvailablePRs.length) {
      setEditSelectedPRs([]);
    } else {
      setEditSelectedPRs(editAvailablePRs.map(pr => pr._id || pr.id));
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" style={{ padding: '24px' }}>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <Typography 
          variant="h4" 
          style={{ 
            fontWeight: 'bold', 
            color: theme.palette.primary.main, 
            marginBottom: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px' 
          }}
        >
          <CampaignIcon style={{ fontSize: '32px' }} />
          Campaigns
        </Typography>
        <Typography variant="body1" style={{ color: theme.palette.text.secondary }}>
          Manage and track your PR campaigns
        </Typography>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </div>
      )}

      {/* Error Banner - Show at top if there's an error */}
      {error && !loading && (
        <Alert 
          severity="warning" 
          style={{ marginBottom: '24px' }}
          action={
            <Button
              size="small"
              onClick={fetchCampaigns}
              style={{ color: 'inherit' }}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Main Content - Show UI even if there's an error (with default values) */}
      {!loading && (
        <>
          {/* Stats Cards */}
          <Grid container spacing={3} style={{ marginBottom: '32px' }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ borderRadius: '12px' }}>
                <CardContent>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <Typography variant="h4" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                        {campaigns.length}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#666' }}>
                        Total Campaigns
                      </Typography>
                    </div>
                    <CampaignIcon style={{ fontSize: '40px', color: '#0927eb', opacity: 0.3 }} />
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ borderRadius: '12px' }}>
                <CardContent>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <Typography variant="h4" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                        {campaigns.filter(c => c.status === 'active').length}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#666' }}>
                        Active
                      </Typography>
                    </div>
                    <PlayIcon style={{ fontSize: '40px', color: '#4CAF50', opacity: 0.3 }} />
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ borderRadius: '12px' }}>
                <CardContent>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <Typography variant="h4" style={{ fontWeight: 'bold', color: '#FF9800' }}>
                        ${campaigns.reduce((sum, c) => sum + c.spent, 0).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#666' }}>
                        Total Spent
                      </Typography>
                    </div>
                    <MoneyIcon style={{ fontSize: '40px', color: '#FF9800', opacity: 0.3 }} />
                  </div>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card style={{ borderRadius: '12px' }}>
                <CardContent>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <Typography variant="h4" style={{ fontWeight: 'bold', color: '#2196F3' }}>
                        {campaigns.reduce((sum, c) => sum + c.impressions, 0).toLocaleString()}
                      </Typography>
                      <Typography variant="body2" style={{ color: '#666' }}>
                        Total Impressions
                      </Typography>
                    </div>
                    <TrendingUpIcon style={{ fontSize: '40px', color: '#2196F3', opacity: 0.3 }} />
                  </div>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

      {/* Filters and Search */}
      <Paper style={{ padding: '24px', marginBottom: '24px', borderRadius: '12px' }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              startIcon={<AnalyticsIcon />}
              fullWidth
              onClick={() => navigate('/analytics')}
              style={{
                borderColor: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#000000',
                color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#000000'
              }}
            >
              Analytics
            </Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              fullWidth
              onClick={() => setCreateDialogOpen(true)}
              style={{
                backgroundColor: '#000000',
                color: 'white',
                fontWeight: 'bold'
              }}
            >
              New Campaign
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Campaigns Table */}
      <TableContainer component={Paper} style={{ borderRadius: '12px' }}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell style={{ fontWeight: 'bold' }}>Campaign</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Budget</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Spent</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Target Audience</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Channels</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Impressions</TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCampaigns
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((campaign) => (
                <TableRow key={campaign.id} hover>
                  <TableCell>
                    <div>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        {campaign.name}
                      </Typography>
                      <div style={{ marginTop: '4px' }}>
                        {campaign.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            style={{ marginRight: '4px', marginBottom: '2px' }}
                          />
                        ))}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(campaign.status)}
                      label={campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                      style={{
                        backgroundColor: getStatusColor(campaign.status),
                        color: 'white',
                        fontWeight: 'bold'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                        ${campaign.budget.toLocaleString()}
                      </Typography>
                      <div style={{ marginTop: '4px' }}>
                        <LinearProgress
                          variant="determinate"
                          value={getBudgetProgress(campaign.spent, campaign.budget)}
                          style={{
                            height: '6px',
                            borderRadius: '3px',
                            backgroundColor: 'rgba(9, 39, 235, 0.2)'
                          }}
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                      ${campaign.spent.toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <TargetIcon style={{ fontSize: '16px', color: '#666' }} />
                      {campaign.targetAudience}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <PublicIcon style={{ fontSize: '16px', color: '#666' }} />
                      {campaign.channels}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <VisibilityIcon style={{ fontSize: '16px', color: '#666' }} />
                      {(campaign.impressions || 0).toLocaleString()}
                    </div>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={(e) => handleMenuClick(e, campaign)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredCampaigns.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewCampaign}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditCampaign}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
          navigate('/analytics');
          handleMenuClose();
        }}>
          <ListItemIcon>
            <AnalyticsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Analytics</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteCampaign} style={{ color: '#F44336' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" style={{ color: '#F44336' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* View Campaign Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <span style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
            {selectedCampaign?.name}
          </span>
        </DialogTitle>
        <DialogContent>
          {selectedCampaign && (
            <div style={{ marginTop: '16px' }}>
              <Grid container spacing={3}>
                {selectedCampaign.description && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                      Description
                    </Typography>
                    <Typography variant="body1" style={{ color: theme.palette.text.secondary }}>
                      {selectedCampaign.description}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                    Status
                  </Typography>
                  <Chip
                    icon={getStatusIcon(selectedCampaign.status)}
                    label={selectedCampaign.status.charAt(0).toUpperCase() + selectedCampaign.status.slice(1)}
                    style={{
                      backgroundColor: getStatusColor(selectedCampaign.status),
                      color: 'white',
                      fontWeight: 'bold'
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                    Target Audience
                  </Typography>
                  <Typography style={{ color: theme.palette.text.secondary }}>{selectedCampaign.targetAudience}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                    Start Date
                  </Typography>
                  <Typography style={{ color: theme.palette.text.secondary }}>
                    {new Date(selectedCampaign.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                    End Date
                  </Typography>
                  <Typography style={{ color: theme.palette.text.secondary }}>
                    {new Date(selectedCampaign.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                    Budget
                  </Typography>
                  <Typography style={{ color: theme.palette.text.secondary }}>${selectedCampaign.budget.toLocaleString()}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                    Spent
                  </Typography>
                  <Typography style={{ color: theme.palette.text.secondary }}>${selectedCampaign.spent.toLocaleString()}</Typography>
                  <LinearProgress
                    variant="determinate"
                    value={getBudgetProgress(selectedCampaign.spent, selectedCampaign.budget)}
                    style={{
                      marginTop: '8px',
                      height: '8px',
                      borderRadius: '4px'
                    }}
                  />
                  <Typography variant="caption" style={{ color: theme.palette.text.secondary, marginTop: '4px', display: 'block' }}>
                    {getBudgetProgress(selectedCampaign.spent, selectedCampaign.budget).toFixed(1)}% of budget used
                  </Typography>
                </Grid>
                {selectedCampaign.tags && selectedCampaign.tags.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                      Tags
                    </Typography>
                    <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedCampaign.tags.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" />
                      ))}
                    </Box>
                  </Grid>
                )}
                {(selectedCampaign as any).pressReleases && Array.isArray((selectedCampaign as any).pressReleases) && (selectedCampaign as any).pressReleases.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                      Press Releases ({((selectedCampaign as any).pressReleases || []).length})
                    </Typography>
                    <Box style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {((selectedCampaign as any).pressReleases || []).map((pr: any, index: number) => (
                        <Box key={pr._id || pr.id || index} style={{ 
                          padding: '8px', 
                          marginBottom: '8px', 
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <ArticleIcon style={{ fontSize: '20px', color: theme.palette.primary.main }} />
                          <Typography variant="body2" style={{ color: theme.palette.text.primary, flex: 1 }}>
                            {pr.title || `Press Release ${index + 1}`}
                          </Typography>
                          <Chip 
                            label={pr.status || 'draft'} 
                            size="small"
                            style={{
                              backgroundColor: pr.status === 'published' ? '#4CAF50' : 
                                             pr.status === 'draft' ? '#9E9E9E' : 
                                             theme.palette.primary.main,
                              color: 'white',
                              fontSize: '0.7rem'
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Paper style={{ padding: '16px', textAlign: 'center' }}>
                        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#0927eb' }}>
                          {(selectedCampaign.impressions || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          Impressions
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper style={{ padding: '16px', textAlign: 'center' }}>
                        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#4CAF50' }}>
                          {(selectedCampaign.clicks || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          Clicks
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper style={{ padding: '16px', textAlign: 'center' }}>
                        <Typography variant="h6" style={{ fontWeight: 'bold', color: '#2196F3' }}>
                          {(selectedCampaign.channels || 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          Channels
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleEditCampaign}
            style={{
              backgroundColor: '#000000',
              color: 'white'
            }}
          >
            Edit Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Campaign Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <span style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
            Create New Campaign
          </span>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} style={{ marginTop: '16px' }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campaign Name"
                placeholder="Enter campaign name"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                placeholder="Enter campaign description"
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={newCampaign.startDate}
                onChange={(e) => setNewCampaign({ ...newCampaign, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={newCampaign.endDate}
                onChange={(e) => setNewCampaign({ ...newCampaign, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Budget"
                type="number"
                placeholder="Enter budget amount"
                value={newCampaign.budget}
                onChange={(e) => setNewCampaign({ ...newCampaign, budget: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Target Audience"
                placeholder="Enter target audience"
                value={newCampaign.targetAudience}
                onChange={(e) => setNewCampaign({ ...newCampaign, targetAudience: e.target.value })}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setNewCampaign({
              name: '',
              description: '',
              startDate: '',
              endDate: '',
              budget: '',
              targetAudience: '',
              tags: []
            });
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCampaign}
            style={{
              backgroundColor: '#000000',
              color: 'white'
            }}
          >
            Create Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Campaign Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setViewDialogOpen(false);
          setEditCampaign({
            name: '',
            description: '',
            startDate: '',
            endDate: '',
            budget: '',
            targetAudience: '',
            tags: []
          });
          setEditSelectedPRs([]);
          setEditAvailablePRs([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <span style={{ fontWeight: 'bold', fontSize: '1.25rem', color: theme.palette.text.primary }}>
            Edit Campaign
          </span>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} style={{ marginTop: '16px' }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campaign Name"
                placeholder="Enter campaign name"
                value={editCampaign.name}
                onChange={(e) => setEditCampaign({ ...editCampaign, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                placeholder="Enter campaign description"
                value={editCampaign.description}
                onChange={(e) => setEditCampaign({ ...editCampaign, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={editCampaign.startDate}
                onChange={(e) => setEditCampaign({ ...editCampaign, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={editCampaign.endDate}
                onChange={(e) => setEditCampaign({ ...editCampaign, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Budget"
                type="number"
                placeholder="Enter budget amount"
                value={editCampaign.budget}
                onChange={(e) => setEditCampaign({ ...editCampaign, budget: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Target Audience"
                placeholder="Enter target audience"
                value={editCampaign.targetAudience}
                onChange={(e) => setEditCampaign({ ...editCampaign, targetAudience: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Divider style={{ margin: '16px 0' }} />
              <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: '16px', color: theme.palette.text.primary }}>
                Select Press Releases
              </Typography>
              {editAvailablePRs.length === 0 ? (
                <Typography variant="body2" style={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                  No press releases available. Create press releases first.
                </Typography>
              ) : (
                <>
                  <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                      {editSelectedPRs.length} of {editAvailablePRs.length} PR{editAvailablePRs.length !== 1 ? 's' : ''} selected
                    </Typography>
                    <Button
                      size="small"
                      variant={editSelectedPRs.length === editAvailablePRs.length ? "contained" : "outlined"}
                      onClick={handleSelectAllEditPRs}
                      style={{
                        textTransform: 'none',
                        fontSize: '0.875rem'
                      }}
                    >
                      {editSelectedPRs.length === editAvailablePRs.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Box>
                  <Box style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '4px',
                    padding: '8px'
                  }}>
                    <Grid container spacing={2}>
                      {editAvailablePRs.map((pr) => {
                        const prId = pr._id || pr.id;
                        const isSelected = editSelectedPRs.includes(String(prId));
                        return (
                          <Grid item xs={12} sm={6} key={prId}>
                            <Card
                              style={{
                                cursor: 'pointer',
                                border: isSelected ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                                backgroundColor: isSelected 
                                  ? (theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.05)')
                                  : 'transparent',
                                transition: 'all 0.2s'
                              }}
                              onClick={() => handleToggleEditPR(String(prId))}
                            >
                              <CardContent style={{ padding: '12px' }}>
                                <Box style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleToggleEditPR(String(prId))}
                                    onClick={(e) => e.stopPropagation()}
                                    size="small"
                                  />
                                  <Box style={{ flex: 1, minWidth: 0 }}>
                                    <Typography 
                                      variant="body2" 
                                      style={{ 
                                        fontWeight: 'bold',
                                        color: theme.palette.text.primary,
                                        marginBottom: '4px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                    >
                                      {pr.title || 'Untitled Press Release'}
                                    </Typography>
                                    <Typography 
                                      variant="caption" 
                                      style={{ 
                                        color: theme.palette.text.secondary,
                                        display: 'block'
                                      }}
                                    >
                                      {pr.status || 'draft'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            setViewDialogOpen(false);
            setEditCampaign({
              name: '',
              description: '',
              startDate: '',
              endDate: '',
              budget: '',
              targetAudience: '',
              tags: []
            });
            setEditSelectedPRs([]);
            setEditAvailablePRs([]);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateCampaign}
            style={{
              backgroundColor: '#000000',
              color: 'white'
            }}
          >
            Update Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* PR Upload and Selection Dialog */}
      <Dialog
        open={prUploadDialogOpen}
        onClose={() => {
          setPrUploadDialogOpen(false);
          setUploadStep(0);
          setCreatedCampaignId(null);
          setSelectedPRs([]);
          setUploadedPRs([]);
          setPasteContent('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <span style={{ fontWeight: 'bold', fontSize: '1.25rem', color: theme.palette.text.primary }}>
            Upload PRs for Campaign
          </span>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={uploadStep} style={{ marginTop: '20px', marginBottom: '30px' }}>
            <Step>
              <StepLabel>Upload PRs</StepLabel>
            </Step>
            <Step>
              <StepLabel>Select PRs to Distribute</StepLabel>
            </Step>
          </Stepper>

          {uploadStep === 0 && (
            <Box>
              <Tabs 
                value={uploadMethod} 
                onChange={(e, newValue) => setUploadMethod(newValue)}
                style={{ marginBottom: '20px' }}
              >
                <Tab label="Upload Files" value="upload" icon={<CloudUploadIcon />} iconPosition="start" />
                <Tab label="Paste Content" value="paste" icon={<ContentPasteIcon />} iconPosition="start" />
              </Tabs>

              {uploadMethod === 'upload' && (
                <Box>
                  <Typography variant="body1" style={{ marginBottom: '16px', color: theme.palette.text.secondary }}>
                    Upload PR files one by one (Word, PDF, or TXT)
                  </Typography>
                  <input
                    accept=".doc,.docx,.pdf,.txt"
                    style={{ display: 'none' }}
                    id="pr-file-upload"
                    multiple
                    type="file"
                    onChange={(e) => handleFileUpload(e.target.files)}
                  />
                  <label htmlFor="pr-file-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      fullWidth
                      disabled={uploading}
                      style={{ marginBottom: '16px' }}
                    >
                      {uploading ? 'Uploading...' : 'Upload PR Files'}
                    </Button>
                  </label>

                  {uploadedPRs.length > 0 && (
                    <Box style={{ marginTop: '20px' }}>
                      <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 'bold', color: theme.palette.text.primary }}>
                        Uploaded PRs:
                      </Typography>
                      <List>
                        {uploadedPRs
                          .filter((pr, index, self) => {
                            const prId = String(pr._id || pr.id).trim();
                            return index === self.findIndex((p) => String(p._id || p.id).trim() === prId);
                          })
                          .map((pr) => {
                            const prId = String(pr._id || pr.id).trim();
                            return (
                              <ListItem key={prId}>
                                <ArticleIcon style={{ marginRight: '8px', color: theme.palette.primary.main }} />
                                <MuiListItemText 
                                  primary={pr.title || 'New Press Release'} 
                                  secondary={`Status: ${pr.status || 'draft'}`}
                                  primaryTypographyProps={{ style: { color: theme.palette.text.primary } }}
                                  secondaryTypographyProps={{ style: { color: theme.palette.text.secondary } }}
                                />
                              </ListItem>
                            );
                          })}
                      </List>
                    </Box>
                  )}

                  {/* Multimedia Upload Section */}
                  <Box style={{ marginTop: '24px', padding: '16px', border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                    <Typography variant="subtitle2" style={{ marginBottom: '12px', fontWeight: 'bold', color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <PhotoLibraryIcon style={{ fontSize: '20px' }} />
                      Upload Multimedia (Images) for PRs
                    </Typography>
                    <Typography variant="body2" style={{ marginBottom: '16px', color: theme.palette.text.secondary }}>
                      Upload images related to your press releases.
                    </Typography>
                    
                    {uploadedPRs.length > 0 ? (
                      <Box>
                        {uploadedPRs
                          .filter((pr, index, self) => {
                            const prId = String(pr._id || pr.id).trim();
                            return index === self.findIndex((p) => String(p._id || p.id).trim() === prId);
                          })
                          .map((pr) => {
                            const prId = String(pr._id || pr.id).trim();
                            const prImages = multimediaFiles[prId] || [];
                            const isUploading = uploadingMultimedia[prId] || false;
                            
                            return (
                              <Box key={prId} style={{ marginBottom: '16px', padding: '12px', backgroundColor: theme.palette.background.paper, borderRadius: '8px' }}>
                                <Typography variant="body2" style={{ marginBottom: '8px', fontWeight: 600, color: theme.palette.text.primary }}>
                                  {pr.title || 'New Press Release'}
                                </Typography>
                                <input
                                  accept="image/*"
                                  style={{ display: 'none' }}
                                  id={`multimedia-upload-${prId}`}
                                  multiple
                                  type="file"
                                  onChange={(e) => handleMultimediaUpload(e.target.files, prId)}
                                  disabled={isUploading}
                                />
                                <label htmlFor={`multimedia-upload-${prId}`}>
                                  <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<PhotoLibraryIcon />}
                                    size="small"
                                    disabled={isUploading}
                                    style={{ marginBottom: '12px' }}
                                  >
                                    {isUploading ? 'Uploading...' : 'Upload Images'}
                                  </Button>
                                </label>
                                
                                {prImages.length > 0 && (
                                  <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                                    {prImages.map((imageUrl, idx) => (
                                      <Box key={idx} style={{ position: 'relative', width: '80px', height: '80px' }}>
                                        <img 
                                          src={imageUrl} 
                                          alt={`PR image ${idx + 1}`}
                                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                        />
                                        <IconButton
                                          size="small"
                                          onClick={() => handleRemoveMultimedia(prId, idx)}
                                          sx={{
                                            position: 'absolute',
                                            top: '-8px',
                                            right: '-8px',
                                            backgroundColor: theme.palette.error.main,
                                            color: 'white',
                                            width: '24px',
                                            height: '24px',
                                            '&:hover': { backgroundColor: theme.palette.error.dark }
                                          }}
                                        >
                                          <DeleteMultimediaIcon style={{ fontSize: '16px' }} />
                                        </IconButton>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                      </Box>
                    ) : (
                      <Typography variant="body2" style={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                        Upload PR files first to add multimedia
                      </Typography>
                    )}
                  </Box>

                  <Box style={{ marginTop: '20px' }}>
                    <Typography variant="body2" style={{ marginBottom: '8px', color: theme.palette.text.secondary }}>
                      Or select from existing PRs:
                    </Typography>
                    <Button
                      variant="text"
                      onClick={async () => {
                        await fetchAvailablePRs();
                        setUploadStep(1);
                      }}
                      disabled={availablePRs.length === 0 && uploadedPRs.length === 0}
                    >
                      Continue to Selection
                    </Button>
                  </Box>
                </Box>
              )}

              {uploadMethod === 'paste' && (
                <Box>
                  <Typography variant="body1" style={{ marginBottom: '16px', color: theme.palette.text.secondary }}>
                    Paste your PR content below
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={10}
                    placeholder="Paste PR content here..."
                    value={pasteContent}
                    onChange={(e) => setPasteContent(e.target.value)}
                    style={{ marginBottom: '16px' }}
                  />
                  <Button
                    variant="contained"
                    onClick={handlePastePR}
                    disabled={uploading || !pasteContent.trim()}
                    fullWidth
                    style={{ marginBottom: '16px' }}
                  >
                    {uploading ? 'Creating PR...' : 'Create PR from Content'}
                  </Button>

                  {uploadedPRs.length > 0 && (
                    <Box style={{ marginTop: '20px' }}>
                      <Typography variant="subtitle2" style={{ marginBottom: '8px', fontWeight: 'bold', color: theme.palette.text.primary }}>
                        Created PRs:
                      </Typography>
                      <List>
                        {uploadedPRs.map((pr) => (
                          <ListItem key={pr._id}>
                            <ArticleIcon style={{ marginRight: '8px', color: theme.palette.primary.main }} />
                            <MuiListItemText 
                              primary={pr.title} 
                              secondary={`Status: ${pr.status}`}
                              primaryTypographyProps={{ style: { color: theme.palette.text.primary } }}
                              secondaryTypographyProps={{ style: { color: theme.palette.text.secondary } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  <Box style={{ marginTop: '20px' }}>
                    <Button
                      variant="text"
                      onClick={async () => {
                        await fetchAvailablePRs();
                        setUploadStep(1);
                      }}
                      disabled={availablePRs.length === 0 && uploadedPRs.length === 0}
                    >
                      Continue to Selection
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {uploadStep === 1 && (
            <Box>
              <Box style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '20px',
                padding: '12px',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: '8px'
              }}>
                <Box>
                  <Typography variant="h6" style={{ fontWeight: 'bold', color: theme.palette.text.primary, marginBottom: '4px' }}>
                    Select PRs to Distribute
                  </Typography>
                  <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                    {selectedPRs.length} of {(() => {
                      // Get unique PR count (excluding duplicates)
                      const uploadedIds = new Set(uploadedPRs.map(pr => String(pr._id || pr.id).trim()));
                      const availableIds = new Set(availablePRs
                        .filter(pr => {
                          const prId = String(pr._id || pr.id).trim();
                          return !uploadedIds.has(prId);
                        })
                        .map(pr => String(pr._id || pr.id).trim())
                      );
                      const totalUnique = uploadedIds.size + availableIds.size;
                      return totalUnique;
                    })()} PR{(() => {
                      const uploadedIds = new Set(uploadedPRs.map(pr => String(pr._id || pr.id).trim()));
                      const availableIds = new Set(availablePRs
                        .filter(pr => {
                          const prId = String(pr._id || pr.id).trim();
                          return !uploadedIds.has(prId);
                        })
                        .map(pr => String(pr._id || pr.id).trim())
                      );
                      return uploadedIds.size + availableIds.size;
                    })() !== 1 ? 's' : ''} selected
                  </Typography>
                </Box>
                <Button
                  variant={(() => {
                    const uploadedIds = new Set(uploadedPRs.map(pr => String(pr._id || pr.id).trim()));
                    const availableIds = new Set(availablePRs
                      .filter(pr => {
                        const prId = String(pr._id || pr.id).trim();
                        return !uploadedIds.has(prId);
                      })
                      .map(pr => String(pr._id || pr.id).trim())
                    );
                    const totalUnique = uploadedIds.size + availableIds.size;
                    return selectedPRs.length === totalUnique ? "contained" : "outlined";
                  })()}
                  size="medium"
                  onClick={handleSelectAllPRs}
                  style={{
                    minWidth: '120px'
                  }}
                >
                  {(() => {
                    const uploadedIds = new Set(uploadedPRs.map(pr => String(pr._id || pr.id).trim()));
                    const availableIds = new Set(availablePRs
                      .filter(pr => {
                        const prId = String(pr._id || pr.id).trim();
                        return !uploadedIds.has(prId);
                      })
                      .map(pr => String(pr._id || pr.id).trim())
                    );
                    const totalUnique = uploadedIds.size + availableIds.size;
                    return selectedPRs.length === totalUnique ? 'Deselect All' : 'Select All';
                  })()}
                </Button>
              </Box>

              <Box style={{ 
                maxHeight: '500px', 
                overflowY: 'auto',
                padding: '8px'
              }}>
                <Grid container spacing={2}>
                  {uploadedPRs
                    .filter((pr, index, self) => {
                      // Remove duplicates based on ID
                      const prId = String(pr._id || pr.id).trim();
                      return index === self.findIndex((p) => String(p._id || p.id).trim() === prId);
                    })
                    .map((pr) => {
                      const prIdValue = String(pr._id || pr.id).trim();
                      const isSelected = selectedPRs.some(
                        selectedId => String(selectedId).trim() === prIdValue
                      );
                    return (
                      <Grid item xs={12} sm={6} md={4} key={pr._id}>
                        <Card
                          onClick={() => handleTogglePR(pr._id)}
                          style={{
                            cursor: 'pointer',
                            border: isSelected 
                              ? `2px solid ${theme.palette.primary.main}` 
                              : `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                            backgroundColor: isSelected 
                              ? (theme.palette.mode === 'dark' ? 'rgba(58, 79, 237, 0.2)' : 'rgba(9, 39, 235, 0.08)')
                              : theme.palette.background.paper,
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            height: '100%'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = theme.palette.primary.main;
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          <CardContent style={{ padding: '16px' }}>
                            <Box style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                              <Checkbox
                                checked={isSelected}
                                style={{ 
                                  padding: 0,
                                  marginRight: '8px'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTogglePR(pr._id);
                                }}
                              />
                              <ArticleIcon 
                                style={{ 
                                  fontSize: '32px', 
                                  color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary 
                                }} 
                              />
                            </Box>
                            <Typography 
                              variant="subtitle1" 
                              style={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.text.primary,
                                marginBottom: '8px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {pr.title || 'Untitled Press Release'}
                            </Typography>
                            <Chip
                              label={pr.status || 'draft'}
                              size="small"
                              style={{
                                backgroundColor: pr.status === 'published' ? '#4CAF50' : 
                                               pr.status === 'draft' ? '#9E9E9E' : 
                                               theme.palette.primary.main,
                                color: 'white',
                                fontSize: '0.7rem'
                              }}
                            />
                            {isSelected && (
                              <Box style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <CheckCircleIcon style={{ color: 'white', fontSize: '16px' }} />
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                  {availablePRs
                    .filter((pr, index, self) => {
                      // Remove duplicates and filter out PRs already in uploadedPRs
                      const prId = String(pr._id || pr.id).trim();
                      const uploadedIds = new Set(uploadedPRs.map(p => String(p._id || p.id).trim()));
                      return !uploadedIds.has(prId) && index === self.findIndex((p) => String(p._id || p.id).trim() === prId);
                    })
                    .map((pr) => {
                      const prIdValue = String(pr._id || pr.id).trim();
                      const isSelected = selectedPRs.some(
                        selectedId => String(selectedId).trim() === prIdValue
                      );
                    return (
                      <Grid item xs={12} sm={6} md={4} key={pr._id}>
                        <Card
                          onClick={() => handleTogglePR(pr._id)}
                          style={{
                            cursor: 'pointer',
                            border: isSelected 
                              ? `2px solid ${theme.palette.primary.main}` 
                              : `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                            backgroundColor: isSelected 
                              ? (theme.palette.mode === 'dark' ? 'rgba(58, 79, 237, 0.2)' : 'rgba(9, 39, 235, 0.08)')
                              : theme.palette.background.paper,
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            height: '100%'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = theme.palette.primary.main;
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          <CardContent style={{ padding: '16px' }}>
                            <Box style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                              <Checkbox
                                checked={isSelected}
                                style={{ 
                                  padding: 0,
                                  marginRight: '8px'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTogglePR(pr._id);
                                }}
                              />
                              <ArticleIcon 
                                style={{ 
                                  fontSize: '32px', 
                                  color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary 
                                }} 
                              />
                            </Box>
                            <Typography 
                              variant="subtitle1" 
                              style={{ 
                                fontWeight: 'bold', 
                                color: theme.palette.text.primary,
                                marginBottom: '8px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {pr.title || 'Untitled Press Release'}
                            </Typography>
                            <Chip
                              label={pr.status || 'draft'}
                              size="small"
                              style={{
                                backgroundColor: pr.status === 'published' ? '#4CAF50' : 
                                               pr.status === 'draft' ? '#9E9E9E' : 
                                               theme.palette.primary.main,
                                color: 'white',
                                fontSize: '0.7rem'
                              }}
                            />
                            {isSelected && (
                              <Box style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <CheckCircleIcon style={{ color: 'white', fontSize: '16px' }} />
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                {uploadedPRs.length === 0 && availablePRs.length === 0 && (
                  <Box style={{ textAlign: 'center', padding: '40px' }}>
                    <ArticleIcon style={{ fontSize: '64px', color: theme.palette.text.secondary, marginBottom: '16px', opacity: 0.5 }} />
                    <Typography variant="body1" style={{ color: theme.palette.text.secondary, marginBottom: '8px' }}>
                      No PRs available
                    </Typography>
                    <Typography variant="body2" style={{ color: theme.palette.text.secondary }}>
                      Please upload or create PRs first
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            if (uploadStep === 0) {
              setPrUploadDialogOpen(false);
              setCreatedCampaignId(null);
              setCreatedCampaignDetails(null);
              setSelectedPRs([]);
              setUploadedPRs([]);
              setPasteContent('');
            } else {
              setUploadStep(0);
            }
          }}>
            {uploadStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          {uploadStep === 0 && (
            <Button
              variant="contained"
              onClick={async () => {
                await fetchAvailablePRs();
                setUploadStep(1);
              }}
              disabled={availablePRs.length === 0 && uploadedPRs.length === 0}
            >
              Continue to Selection
            </Button>
          )}
          {uploadStep === 1 && (
            <Button
              variant="contained"
              onClick={handleProceedToDistribution}
              disabled={selectedPRs.length === 0}
            >
              Distribute Selected PRs
            </Button>
          )}
        </DialogActions>
      </Dialog>
        </>
      )}
    </Container>
  );
};

export default Campaigns;